
import React, { useEffect, useRef, useState } from 'react'
import moment from 'moment'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'components/Luxkit/Typography/BodyText'
import LineCheckCircleIcon from 'components/Luxkit/Icons/line/LineCheckCircleIcon'
import LineTimesCircleIcon from 'components/Luxkit/Icons/line/LineTimesCircleIcon'
import LineUsersAltIcon from 'components/Luxkit/Icons/line/LineUsersAltIcon'
import LineCalendarIcon from 'components/Luxkit/Icons/line/LineCalendarIcon'
import LineBedIcon from 'components/Luxkit/Icons/line/LineBedIcon'
import NumberRating from 'components/Common/NumberRating/NumberRating'
import Group from 'components/utils/Group'
import { COMPANIONSHIP_ENUM, DEFINITION_ENUM } from 'constants/review'
import { mediaQueryUp } from 'components/utils/breakpoint'
import config from 'constants/config'
import LuxuryEscapesSmallLogo from 'components/App/Header/Logo/logos/LuxuryEscapesSmallLogo'
import LogoIcon from 'components/App/Header/Logo/LogoIcon'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import debounce from 'debounce-promise'
import { pluralizeToString } from 'lib/pluralize'
import UserReviewGallery from './UserReviewGallery'
import Image from 'components/Common/Image/Image'
import { getRecommendsLabel } from 'lib/reviewUtils'

const Root = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: ${rem(24)};
  border-top: 1px solid ${({ theme }) => theme.palette.neutral.six};

  > *:nth-child(2) {
    overflow: hidden;
  }

  ${mediaQueryUp.sm} {
    flex-direction: row;
    gap: ${rem(20)};

    > *:first-child {
      width: ${rem(200)};
    }
   }
`

const ReviewDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
`

const ReviewContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  flex: 1;
`

const TagGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(8)};
  margin-top: ${rem(8)};
`

const ResponseContainer = styled.div`
  background-color: ${({ theme }) => theme.palette.neutral.seven};
  padding: ${rem(20)};
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  ${mediaQueryUp.sm} {
    grid-column: 1 / -1;
  }
`

const LELogo = styled(LuxuryEscapesSmallLogo)`
  fill: ${props => props.theme.palette.neutral.one};
  width: ${rem(20)};
`

const WhitelabelLogoWrapper = styled.div`
  display: flex;
  svg {
    max-width: 60px;
  }
`

const Chip = styled.div`
  display: flex;
  align-items: center;
  height: ${rem(28)};
  padding: 0 ${rem(8)};
  border-radius: ${props => props.theme.borderRadius.round};
  box-shadow: inset 0 0 0 1px ${props => props.theme.palette.neutral.five};
`

const ImagesContainer = styled(Group)`
  margin-top: ${rem(16)};
  padding-bottom: ${rem(8)};
  overflow: auto;
`

const ImageWrapper = styled.div`
  position: relative;
  width: ${rem(88)};
  height: ${rem(88)};
  min-width: ${rem(88)};
  min-height: ${rem(88)};
  cursor: pointer;
`

interface Props {
  userReview: App.Review;
  className?: string;
}

function UserReviewTile(props: Props) {
  const { userReview, className } = props
  const [contentHeight, setContentHeight] = useState<number>(0)
  const [viewPhoto, setViewPhoto] = useState<number>(-1)
  const contentElement = useRef<HTMLDivElement>(null)

  const userCountry = config.regions.find(region => region.code === userReview.userCountry)
  const recommendsLabel = getRecommendsLabel(userReview)

  useEffect(() => {
    const updateHeight = () => {
      setContentHeight(contentElement.current?.clientHeight ?? 0)
    }
    const handleResize = debounce(updateHeight, 1000)

    updateHeight()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return (
    <Root className={className}>
      <Group direction="horizontal" horizontalAlign="space-between">
        <ReviewDetails>
          <div>
            <BodyText weight="bold" variant="large">{userReview.userName}</BodyText>
            {userCountry && userReview.userName !== 'Anonymous' && <BodyText variant="medium">{userCountry.name}</BodyText>}
            {userReview.userName !== 'Anonymous' && (
              <BodyText variant="medium">
                {!!userReview.userOrdersCount && pluralizeToString('escape', userReview.userOrdersCount)}
                {userReview.userOrdersCount && userReview.userReviewsCount ? ' · ' : ''}
                {!!userReview.userReviewsCount && pluralizeToString('review', userReview.userReviewsCount)}
              </BodyText>
            )}
            <BodyText variant="medium" colour="neutral-three">Reviewed {moment(userReview.createdAt).format('DD MMM YYYY')}</BodyText>
          </div>
          <div>
            {userReview.companionship && (
              <Group direction="horizontal" verticalAlign="center" gap={4}>
                <LineUsersAltIcon size="S" />
                <BodyText variant="small">{COMPANIONSHIP_ENUM[userReview.companionship]}</BodyText>
              </Group>
            )}
            {userReview.offerRoomName && (
              <Group direction="horizontal" verticalAlign="center" gap={4}>
                <LineBedIcon size="S" />
                <BodyText variant="small">{userReview.offerRoomName}</BodyText>
              </Group>
            )}
            {!!(userReview.orderNumberOfNights || userReview.reservationCheckinDate) && (
              <Group direction="horizontal" verticalAlign="center" gap={4}>
                <LineCalendarIcon size="S" />
                <BodyText variant="small">
                  {!!userReview.orderNumberOfNights && pluralizeToString('night', userReview.orderNumberOfNights)}
                  {userReview.orderNumberOfNights && userReview.reservationCheckinDate ? ' · ' : ''}
                  {!!userReview.reservationCheckinDate && moment(userReview.reservationCheckinDate).format('MMM YYYY')}
                </BodyText>
              </Group>
            )}
          </div>
        </ReviewDetails>
        <CSSBreakpoint max="xs">
          <div>
            <NumberRating onlyNumber variant="large" rating={userReview.rating} />
          </div>
        </CSSBreakpoint>
      </Group>
      <ReviewContent>
        <Group direction="horizontal" horizontalAlign="space-between" gap={8}>
          <ReviewContent>
            <LargeContentToggle
              maxHeight={contentHeight > 240 ? 120 : 240} // Show "Read more" only if greater than 10 lines (240)
              showMoreText="Read more"
              showLessText="Read less"
            >
              <div ref={contentElement}>
                <BodyText variant="large">{userReview.review}</BodyText>
              </div>
            </LargeContentToggle>
            {recommendsLabel && (
              <Group direction="horizontal" verticalAlign="center" gap={4}>
                {userReview.rating >= 6 ? <LineCheckCircleIcon size="XS" colour="success" /> : <LineTimesCircleIcon size="XS" />}
                <BodyText variant="small" weight="bold">{recommendsLabel}</BodyText>
              </Group>
            )}
          </ReviewContent>

          <CSSBreakpoint min="sm">
            <div>
              <NumberRating onlyNumber variant="large" rating={userReview.rating} />
            </div>
          </CSSBreakpoint>
        </Group>

        {!!userReview.definition?.length && (
          <TagGroup>
            {userReview.definition.map(definition => (
              <Chip key={definition}>
                <BodyText variant="small">{DEFINITION_ENUM[definition]}</BodyText>
              </Chip>
            ))}
          </TagGroup>
        )}

        {!!userReview.photos?.length && (
          <>
            <UserReviewGallery
              isOpen={viewPhoto >= 0}
              initialIndex={viewPhoto}
              review={userReview}
              onRequestClose={() => setViewPhoto(-1)}
            />
            <ImagesContainer direction="horizontal" gap={16}>
              {[...userReview.photos].map((photo, index) => (
                <ImageWrapper
                  key={photo.imageId}
                  onClick={() => setViewPhoto(index)}
                >
                  <Image
                    id={photo.imageId}
                    alt={photo.imageId}
                    width={88}
                    height={88}
                    fit="center"
                    quality="good"
                  />
                </ImageWrapper>
              ))}
            </ImagesContainer>
          </>
        )}

        {userReview.response &&
          <ResponseContainer>
            <Group direction="horizontal" verticalAlign="center" gap={4}>
              {config.BRAND === 'luxuryescapes' && <LELogo />}
              {config.BRAND !== 'luxuryescapes' &&
                <WhitelabelLogoWrapper>
                  <LogoIcon />
                </WhitelabelLogoWrapper>
              }
              <BodyText variant="medium" weight="bold">Response from {config.title}</BodyText>
            </Group>
            <BodyText variant="medium" colour="neutral-three">{moment(userReview.response.createdAt).format('DD MMM YYYY')}</BodyText>
            <BodyText variant="large" colour="neutral-two">{userReview.response.message}</BodyText>
          </ResponseContainer>
        }
      </ReviewContent>
    </Root>
  )
}

export default UserReviewTile
